import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Switch,
  Typography,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  DialogTitle,
} from "@mui/material";
import { Circle } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { MessageIcon, StreamsIcon } from "../../components/icons";

import { tokens } from "../../theme";
import { getStatusColors } from "../../constants/statusColors";
import apiService from "../../service/Api";
import { ChecklistRow, SectionPayload } from "./Checklists";
import SearchRoleDialog, { RoleItem } from "./components/SearchRoleDialog";
import { toast } from "react-toastify";

type TaskItem = {
  id: number;
  name: string;
  description?: string | null;
  points?: number;
};

type Task = {
  id: number;
  title: string; // section name
  description: string;
  inputType: string;

  completion: number;
  items: TaskItem[];
};

type ChecklistProps = {
  id: number;
  title: string;
  role: string;
  type: number;
  application: "pre_stream" | "on_stream" | "post_stream";
  roleId: number | null;
  active: boolean;
  usage: number;
  compliance: number;
  lastUpdate: {
    date: string;
    updatedBy: string;
  };
  tasks: Task[];
};

interface ChecklistApiResponse {
  id: number;
  name: string;
  roles_name: string;
  roles?: number | null;
  checklist_type?: number;
  phase: string;
  active?: boolean;
  updated_at: string;
  sections: {
    id: number;
    name: string; // ✅ TASK TITLE
    description: string | null;
    list_items: {
      id: number;
      name: string;
      description: string | null;
    }[];
  }[];
}
interface UsageStatsResponse {
  times_used: number;
  compliance_avg: number;
  most_skipped: { name: string; completion_percent: number } | null;
  recent_usage: {
    crew_member_name: string;
    stream_id: number | null;
    stream_title: string | null;
    date: string;
    completion_percent: number;
    tasks_completed: number;
    tasks_total: number;
  }[];
}

const ViewChecklist: React.FC = () => {
  const [usageStats, setUsageStats] = useState<UsageStatsResponse | null>(null);
  const [checklist, setChecklist] = useState<ChecklistProps | undefined>(
    undefined,
  );
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleItem | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateName, setDuplicateName] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const getChecklistById = async (
    id: number,
  ): Promise<ChecklistApiResponse> => {
    const response = await apiService.get(`/api/checklists/checklist/${id}/`);

    return response.data as ChecklistApiResponse;
  };

  const fetchChecklist = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getChecklistById(Number(id));

      const formatted: ChecklistProps = {
        id: Number(data.id),
        title: data.name,
        role: data.roles_name,
        roleId: data.roles ?? null,
        type: Number(data.checklist_type ?? 0),
        application: data.phase as "pre_stream" | "on_stream" | "post_stream",
        active: data.active ?? true,
        usage: data.sections?.length ?? 0,
        compliance: 0,
        lastUpdate: {
          date: data.updated_at,
          updatedBy: "System",
        },
        tasks: (data.sections ?? []).map((section) => ({
          id: section.id,
          title: section.name,
          description: section.description ?? "",
          inputType: "checklist",

          completion: 0,
          items: section.list_items ?? [],
        })),
      };

      setChecklist(formatted);
    } catch (error) {
      console.error("Failed to fetch checklist:", error);
    }
  }, [id]);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);
  useEffect(() => {
    const ChecklistItemTest = async () => {
      try {
        await apiService.get(`/api/checklists/checklist-item/${id}/`);
      } catch (error) {
        console.log(
          "Test endpoint hit (expected failure if backend not setup)",
          error,
        );
      }
    };

    if (id) {
      ChecklistItemTest();
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;

    const fetchUsageStats = async () => {
      try {
        const res = await apiService.get<UsageStatsResponse>(
          `/api/checklists/checklist/${id}/usage-stats/`,
        );
        setUsageStats(res.data);
      } catch (error) {
        console.error("Failed to fetch usage stats:", error);
      }
    };

    fetchUsageStats();
  }, [id]);

  const openDuplicateDialog = () => {
    if (!checklist) return;
    setDuplicateName(`${checklist.title} (Copy)`);
    setDuplicateDialogOpen(true);
  };

  const handleConfirmDuplicate = async () => {
    if (!checklist || !duplicateName.trim()) return;

    const payload = {
      name: duplicateName.trim(),
      description: null,
      roles: checklist.roleId,
      phase: checklist.application,
      notes: null,
      sections: checklist.tasks.map((task, sectionIndex) => ({
        name: task.title,
        description: task.description || null,
        order: sectionIndex,
        list_items: task.items.map((item, itemIndex) => ({
          name: item.name,
          description: item.description || null,
          points: item.points ?? 1,
          order: itemIndex,
        })),
      })),
    };

    setDuplicating(true);
    try {
      const res = await apiService.post<{ id: number }>(
        "/api/checklists/checklist/full-create/",
        payload,
      );
      toast.success(`"${duplicateName}" created.`);

      setDuplicateDialogOpen(false);
      navigate(`/crew-management/checklists/${res.data.id}`);
    } catch (error) {
      toast.error("Failed to duplicate checklist — please try again.");

      console.error("Failed to duplicate checklist:", error);
    } finally {
      setDuplicating(false);
    }
  };

  const editChecklistRow: ChecklistRow | null = checklist
    ? {
        id: checklist.id,
        name: checklist.title,
        description: null, // ViewChecklist doesn't currently store description — see note below
        roleId: checklist.roleId,
        roleName: checklist.role,
        phase: checklist.application,
        sections: checklist.tasks.map(
          (task, i): SectionPayload => ({
            id: task.id,
            name: task.title,
            description: task.description || null,
            order: i,
            list_items: task.items.map((item) => ({
              id: item.id,
              name: item.name,
              description: item.description ?? null,
              points: item.points ?? 1,
            })),
          }),
        ),
        updatedAt: checklist.lastUpdate.date,
      }
    : null;
  const getApplicationChipProps = (
    application: "pre_stream" | "on_stream" | "post_stream",
  ) => {
    switch (application) {
      case "pre_stream":
        return {
          label: "Pre Stream",
          bgcolor: "#F79E1B40",
          color: "#F79E1B",
        };

      case "on_stream":
        return {
          label: "On Stream",
          bgcolor: "#4BA66340",
          color: "#4BA663",
        };

      case "post_stream":
        return {
          label: "Post Stream",
          bgcolor: "#1E88E540",
          color: "#1E88E5",
        };

      default:
        return {
          label: "Unknown",
          bgcolor: status.pending.bg,
          color: status.pending.fg,
        };
    }
  };

  const getPercentageProps = (val: number) => {
    if (val >= 80) {
      return "#4BA663";
    } else if (val < 80 && val >= 65) {
      return "#0000FF";
    } else if (val < 65 && val >= 50) {
      return "#F79E1B";
    } else if (val < 50) {
      return "#EB4335";
    }
  };

  const getComplianceInsight = (avg: number) => {
    if (avg >= 80) {
      return {
        label: "High Compliance",
        message: "Above average completion rate across all pilots",
        color: "#4BA663",
        bg: "#4BA66310",
      };
    }
    if (avg >= 50) {
      return {
        label: "Moderate Compliance",
        message: "Completion is inconsistent — worth a closer look",
        color: "#F79E1B",
        bg: "#F79E1B10",
      };
    }
    return {
      label: "Low Compliance",
      message: "Most pilots are not completing this checklist",
      color: "#EB4335",
      bg: "#EB433510",
    };
  };

  const getSkippedInsight = (
    mostSkipped: { name: string; completion_percent: number } | null,
  ) => {
    if (!mostSkipped) {
      return {
        label: "No Data Yet",
        message: "This checklist hasn't been used enough to flag skipped tasks",
        color: "#BDBDBD",
        bg: "#BDBDBD10",
      };
    }
    if (mostSkipped.completion_percent < 70) {
      return {
        label: "Frequently Skipped Task",
        message: `"${mostSkipped.name}" is only completed ${mostSkipped.completion_percent}% of the time`,
        color: "#F79E1B",
        bg: "#F79E1B10",
      };
    }
    return {
      label: "No Problem Tasks",
      message: "Every task is being completed consistently",
      color: "#4BA663",
      bg: "#4BA66310",
    };
  };

  const getUsageInsight = (timesUsed: number) => {
    if (timesUsed === 0) {
      return {
        label: "Not Used Yet",
        message: "This checklist hasn't been assigned to any stream",
        color: "#BDBDBD",
        bg: "#BDBDBD10",
      };
    }
    if (timesUsed >= 10) {
      return {
        label: "Frequently Used",
        message: `Assigned ${timesUsed} times — a core part of the workflow`,
        color: "#1E88E5",
        bg: "#1E88E510",
      };
    }
    return {
      label: "In Use",
      message: `Assigned ${timesUsed} time${timesUsed === 1 ? "" : "s"} so far`,
      color: "#1E88E5",
      bg: "#1E88E510",
    };
  };

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const status = getStatusColors(theme.palette.mode);

  const backgroundColor = colors.primary[800] || "#A9B2C3";

  useEffect(() => {
    if (!id) return;

    const fetchChecklist = async () => {
      try {
        const data = await getChecklistById(Number(id));

        console.log("SECTIONS RECEIVED:", data.sections);

        const formatted: ChecklistProps = {
          id: Number(data.id),
          title: data.name,
          role: data.roles_name,
          roleId: data.roles ?? null,
          type: Number(data.checklist_type ?? 0),
          application: data.phase as "pre_stream" | "on_stream" | "post_stream",
          active: true,
          usage: data.sections?.length ?? 0,
          compliance: 0,
          lastUpdate: {
            date: data.updated_at,
            updatedBy: "System",
          },
          tasks: (data.sections ?? []).map((section) => ({
            id: section.id,
            title: section.name,
            description: section.description ?? "",
            inputType: "checklist",

            completion: 0,
            items: section.list_items ?? [], // ✅ ADD THIS
          })),
        };

        console.log(" MAPPED CHECKLIST:", formatted);
        console.log(" MAPPED TASKS:", formatted.tasks);
        setChecklist(formatted); // ✅ no TS error now
      } catch (error) {
        toast.error("Failed to load this checklist.");
        console.error("Failed to fetch checklist:", error);
      }
    };

    fetchChecklist();
  }, [id]);

  console.log({ checklist });

  if (!id || !checklist) return null;

  const { date, updatedBy } = checklist.lastUpdate;

  return (
    <motion.div
      initial={{ opacity: 0, x: 300 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 300 }}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{ minHeight: "100vh", position: "relative" }}>
        {/* Pilot Banner */}
        <Card
          sx={{
            mb: "16px",
            boxShadow: 2,
            height: "100%",
            position: "sticky",
            transition: "1s ease-in-out",
            zIndex: 99,
            top: -16,
          }}
        >
          <CardContent>
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
            >
              <Box display={"flex"} alignItems={"center"} gap={0.4}>
                <Typography variant="h5" sx={{ fontWeight: "bold" }}>
                  {checklist.title}
                </Typography>
                <Chip
                  label={getApplicationChipProps(checklist.application).label}
                  size={"small"}
                  sx={{
                    color: getApplicationChipProps(checklist.application).color,
                    borderRadius: 1,
                    fontWeight: 600,
                    bgcolor: getApplicationChipProps(checklist.application)
                      .bgcolor,
                  }}
                />
              </Box>
              <Box display={"flex"} alignItems={"center"} gap={1}>
                <Button
                  variant="outlined"
                  startIcon={
                    <StreamsIcon color="inherit" sx={{ fontSize: 20 }} />
                  }
                  sx={{ borderRadius: 1.5, textTransform: "none" }}
                  onClick={() => setEditDialogOpen(true)}
                >
                  Edit
                </Button>
                <Button
                  variant="outlined"
                  startIcon={
                    <MessageIcon color="inherit" sx={{ fontSize: 20 }} />
                  }
                  sx={{ borderRadius: 1.5, textTransform: "none" }}
                  onClick={openDuplicateDialog}
                >
                  Duplicate
                </Button>
                <Box display={"flex"} alignItems={"center"} gap={0.4}>
                  <Switch
                    checked={checklist.active}
                    // onChange={handleChange}
                    color={checklist.active ? "primary" : "default"}
                    slotProps={{ input: { "aria-label": "controlled" } }}
                  />
                  <Typography variant={"subtitle2"}>
                    {checklist.active ? "Deactivate" : "Activate"}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box display={"flex"} alignItems={"center"} gap={1}>
              <Chip
                label={checklist.role}
                size={"small"}
                sx={{
                  color: colors.primary[700],
                  borderRadius: 1,
                  fontWeight: 600,
                  bgcolor: "#BDBDBD40",
                }}
              />
              <Chip
                label={checklist.active ? "Active" : "Inactive"}
                icon={<Circle color={"inherit"} sx={{ width: "8px" }} />}
                size={"small"}
                sx={{
                  color: checklist.active ? "#4BA663" : "#F79E1B",
                  borderRadius: 1,
                  fontWeight: 600,
                  bgcolor: checklist.active ? "#4BA66340" : "#F79E1B40",
                }}
              />
            </Box>

            <Typography sx={{ mt: 2 }} variant={"subtitle2"}>
              {`Last updated: ${format(
                new Date(date),
                "dd MMMM yyyy",
              )} by ${updatedBy}`}
            </Typography>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          {/* Checklist Tasks */}
          <Grid size={8}>
            <Card sx={{ boxShadow: 0, mb: 2 }}>
              <CardContent>
                <Box
                  mb={2}
                  sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Task Items
                  </Typography>
                  <Box display={"flex"} alignItems={"center"} gap={0.4}>
                    <Typography variant="caption">
                      Checklist usage during the last 10 events
                    </Typography>
                    <Circle sx={{ fontSize: "6px" }} />
                    <Typography variant="caption">
                      Ordered by priority
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 2 }}>
                  {checklist.tasks?.map((tsk) => {
                    return (
                      <Accordion
                        key={tsk.id}
                        defaultExpanded
                        sx={{
                          mb: 1,
                          border: "1px solid",
                          borderColor: "divider",
                          boxShadow: "none",
                          "&:before": { display: "none" },
                        }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="center"
                              sx={{
                                bgcolor: "#BDBDBD40",
                                borderRadius: 1,
                                padding: 0.8,
                                color: colors.primary[700],
                              }}
                            >
                              <StreamsIcon
                                color="inherit"
                                sx={{ fontSize: 18 }}
                              />
                            </Box>

                            <Box>
                              <Typography variant="subtitle2" fontWeight={600}>
                                {tsk.title}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={0.4}>
                                <Typography variant="caption">
                                  {tsk.inputType}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ pt: 0 }}>
                          <Box display="flex" flexDirection="column" gap={1}>
                            {tsk.items.map((item) => (
                              <Box
                                key={item.id}
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{
                                  px: 1.5,
                                  py: 1,
                                  borderRadius: 1,
                                  bgcolor: colors.primary[800],
                                  border: "1px solid",
                                  borderColor: "divider",
                                  transition: "background-color 0.2s",
                                  "&:hover": {
                                    bgcolor: "action.hover",
                                  },
                                }}
                              >
                                {/* Left side */}
                                <Box display="flex" alignItems="center" gap={1}>
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      bgcolor: "text.secondary",
                                    }}
                                  />
                                  <Typography variant="body2" fontWeight={500}>
                                    {item.name}
                                  </Typography>
                                </Box>

                                {/* Right side – Points */}
                                <Typography
                                  variant="caption"
                                  fontWeight={600}
                                  color="text.secondary"
                                  sx={{
                                    minWidth: 48,
                                    textAlign: "right",
                                  }}
                                >
                                  {item.points ? `${item.points} pts` : "—"}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        </AccordionDetails>
                      </Accordion>
                    );
                  })}
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ boxShadow: 0, mb: 2 }}>
              <CardContent>
                <Box
                  mb={2}
                  sx={{ borderBottom: "1px solid", borderColor: "divider" }}
                >
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Recent Usage
                  </Typography>
                  <Typography variant="caption">
                    Checklist usage during the last 10 events
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  {/* Recent Checklist Usage Table Headings */}
                  <Grid
                    container
                    spacing={2}
                    sx={{
                      mb: "4px",
                      bgcolor: backgroundColor,
                      padding: 1,
                      borderRadius: 1,
                    }}
                  >
                    <Grid size={4} sx={{ textTransform: "uppercase" }}>
                      <Typography
                        variant={"subtitle2"}
                        sx={{ fontWeight: 600 }}
                      >
                        Event
                      </Typography>
                    </Grid>
                    <Grid size={4} sx={{ textTransform: "uppercase" }}>
                      <Typography
                        variant={"subtitle2"}
                        sx={{ fontWeight: 600 }}
                      >
                        Crew Member
                      </Typography>
                    </Grid>
                    <Grid size={2} sx={{ textTransform: "uppercase" }}>
                      <Typography
                        variant={"subtitle2"}
                        sx={{ fontWeight: 600 }}
                      >
                        Date
                      </Typography>
                    </Grid>
                    <Grid size={2} sx={{ textTransform: "uppercase" }}>
                      <Typography
                        variant={"subtitle2"}
                        sx={{ fontWeight: 600 }}
                      >
                        Completion
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Recent Checklist Usage Table Rows */}
                  <Box>
                    {usageStats?.recent_usage.map((row, index) => {
                      const rowBg =
                        index % 2 !== 0 ? backgroundColor : "transparent";
                      return (
                        <Grid
                          key={`${row.stream_id}-${row.crew_member_name}`}
                          container
                          spacing={2}
                          sx={{
                            my: 1,
                            bgcolor: rowBg,
                            padding: 1,
                            borderRadius: 1,
                          }}
                        >
                          <Grid size={4}>
                            <Typography variant="subtitle2">
                              {row.stream_title ?? "N/A"}
                            </Typography>
                          </Grid>
                          <Grid size={4}>
                            <Typography variant="subtitle2">
                              {row.crew_member_name}
                            </Typography>
                          </Grid>
                          <Grid size={2}>
                            <Typography variant="caption">
                              {format(new Date(row.date), "dd MMM yyyy")}
                            </Typography>
                          </Grid>
                          <Grid size={2}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: getPercentageProps(
                                  row.completion_percent,
                                ),
                              }}
                            >
                              {`${row.completion_percent}%`}
                            </Typography>
                          </Grid>
                        </Grid>
                      );
                    })}
                    {(!usageStats || usageStats.recent_usage.length === 0) && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ p: 2 }}
                      >
                        This checklist has not been used on any stream yet.
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Usage Stats & Perfomance Insights */}
          <Grid size={4}>
            <Card sx={{ boxShadow: 0, mb: 2 }}>
              <CardContent>
                <Box mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Usage Statistics
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Box
                    display={"flex"}
                    mb={1}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Times Used
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {usageStats?.times_used ?? 0}
                    </Typography>
                  </Box>

                  <Box
                    display={"flex"}
                    mb={1}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Compliance Avg.
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: getPercentageProps(
                          usageStats?.compliance_avg ?? 0,
                        ),
                        fontWeight: 600,
                      }}
                    >
                      {`${usageStats?.compliance_avg ?? 0}%`}
                    </Typography>
                  </Box>

                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"space-between"}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Most Skipped
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, color: "#EB4335" }}
                    >
                      {usageStats?.most_skipped
                        ? `${usageStats.most_skipped.name} — ${usageStats.most_skipped.completion_percent}%`
                        : "No data yet"}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ boxShadow: 0 }}>
              <CardContent>
                <Box mb={2}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Perfomance Insights
                  </Typography>
                </Box>

                {[
                  getComplianceInsight(usageStats?.compliance_avg ?? 0),
                  getSkippedInsight(usageStats?.most_skipped ?? null),
                  getUsageInsight(usageStats?.times_used ?? 0),
                ].map((insight) => (
                  <Box key={insight.label} sx={{ mb: 1 }}>
                    <Box
                      sx={{
                        bgcolor: insight.bg,
                        borderRadius: 2,
                        border: "1px solid",
                        padding: 1,
                        borderColor: insight.color,
                        color: insight.color,
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={600}>
                        {insight.label}
                      </Typography>
                      <Typography variant="caption">
                        {insight.message}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* All Dialogs */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Duplicate Checklist</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This creates a new checklist with the same sections and items. You
            can rename it now, and adjust anything else afterwards.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Checklist name"
            value={duplicateName}
            onChange={(e) => setDuplicateName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && duplicateName.trim() && !duplicating) {
                handleConfirmDuplicate();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDuplicateDialogOpen(false)}
            disabled={duplicating}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmDuplicate}
            disabled={!duplicateName.trim() || duplicating}
          >
            {duplicating ? "Duplicating..." : "Duplicate"}
          </Button>
        </DialogActions>
      </Dialog>
      <SearchRoleDialog
        searchRoleOpen={editDialogOpen}
        setSearchRoleOpen={setEditDialogOpen}
        searchResults={selectedRole}
        setSearchResults={setSelectedRole}
        refreshChecklists={fetchChecklist}
        editChecklist={editChecklistRow}
        mode="edit"
      />
    </motion.div>
  );
};

export default ViewChecklist;
